import {Mail} from 'lucide-react';
import {PhoneHQButton, PhoneHQInput} from 'ch3-ui-lib';
import {Controller, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import {showToast} from "../../../store/features/Notifications/notificationsSlice.tsx";
import {useStartIntegrationGoogleMutation} from "../../../store/features/integrations/integrationsService.ts";
import {useAppDispatch} from "../../../store/store.ts";
import {validateEmail} from "../../../utilis/commonValidators";
import {getGoogleIntegrationErrorMessage} from "./googleIntegrationErrors.ts";

interface GoogleIntegrationForm {
    email: string;
}

const CONNECT_ERROR_MESSAGE =
    'We could not connect Google Workspace. Try again, or contact PhoneHQ Support if the problem continues.';

export const IntegrationGoogle = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [startIntegrationGoogle, {isLoading}] = useStartIntegrationGoogleMutation();
    const {
        control,
        handleSubmit,
        formState: {errors},
    } = useForm<GoogleIntegrationForm>({
        mode: 'onBlur',
        defaultValues: {email: ''},
    });

    const onSubmit = async (data: GoogleIntegrationForm) => {
        try {
            await startIntegrationGoogle({email: data.email}).unwrap();
            navigate('/admin/integrations/google/list');
        } catch (error: unknown) {
            dispatch(showToast({
                title: 'Could not connect Google Workspace',
                description: getGoogleIntegrationErrorMessage(error, CONNECT_ERROR_MESSAGE),
                type: 'error',
            }));
        }
    };

    return (
        <main className='mx-auto max-w-[580px]'>
            <h2 className="text-2xl font-bold leading-none">Connect Your Google Account</h2>
            <p className="mt-4 text-base leading-6 mb-8">
                Enter the Google administrator email used to install PhoneHQ.
            </p>
            <Controller
                name="email"
                control={control}
                rules={{
                    required: 'Email is required',
                    validate: {
                        isValid: (value: string) => validateEmail(value) || 'Invalid email address',
                    },
                }}
                render={({field}) => (
                    <PhoneHQInput
                        phq-id='integrationGoogle-emailInput'
                        {...field}
                        value={field.value}
                        onChange={({target: {value}}) => field.onChange(value)}
                        containerClassName='max-w-full'
                        prefixIcon={<Mail size={16} aria-hidden="true" className="!p-0"/>}
                        label='E-mail'
                        error={errors.email?.message}
                    />
                )}
            />

            <PhoneHQButton loading={isLoading} onClick={handleSubmit(onSubmit)} className='ml-auto mt-8 min-w-[140px]' phq-id='integrationGoogle-beginSyncBtn'>Begin Sync</PhoneHQButton>
        </main>
    );
};
